import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { BookOpen, Plus, ChevronDown, ChevronUp, ClipboardCheck } from "lucide-react";
import api from "../../api";
import AssignToolModal from "./AssignToolModal";
import { TYPE_LABEL, TYPE_STYLE } from "./ToolkitPreview";

// Homework on the patient file and the consultation page (backend
// clinical/care_plan/toolkit_routes.py). `session` ({consultationId, appointmentId})
// = consultation mode: "To review today" (earlier homework still open, or
// finished since the previous session; view only, never re-linked) above
// what was given in this session, which new homework is linked to;
// `noteText` feeds the suggestions.
const STATUS = {
  assigned: ["Not started", "bg-secondary-100 text-secondary-700"],
  started: ["In progress", "bg-sky-100 text-sky-800"],
  completed: ["Done", "bg-emerald-100 text-emerald-800"],
  cancelled: ["Withdrawn", "bg-secondary-100 text-secondary-500 line-through"],
};
// Staff always see English; this says which language the patient got it in.
const LANG = { hi: "Hindi", mr: "Marathi", ta: "Tamil", te: "Telugu", kn: "Kannada", ml: "Malayalam", bn: "Bengali", gu: "Gujarati", pa: "Punjabi", or: "Odia", ur: "Urdu" };
const fmt = (iso) => (iso ? new Date(iso.length === 10 ? `${iso}T00:00:00` : iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "");

// One entry: short answers on one wrapping line, long text underneath.
const SHORT = new Set(["scale", "number", "date", "time", "datetime", "choice", "multichoice", "text"]);
export function formatAnswer(field, value) {
  if (value === undefined || value === null || value === "") return null;
  if (field.type === "scale") return `${value}/${field.max}`;
  if (Array.isArray(value)) return value.join(", ");
  if (field.type === "datetime") return new Date(value).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
  if (field.type === "date") return new Date(`${value}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  if (field.type === "time") { const [h, m] = String(value).split(":").map(Number); return new Date(2000, 0, 1, h, m || 0).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" }); }
  return String(value);
}

function Entry({ fields, answers, at }) {
  const short = fields.filter((f) => SHORT.has(f.type || "text") && formatAnswer(f, answers[f.key]) !== null);
  const long = fields.filter((f) => !SHORT.has(f.type || "text") && formatAnswer(f, answers[f.key]) !== null);
  return (
    <div className="rounded-lg border border-secondary-100 px-3 py-2">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-secondary-500">{new Date(at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}</span>
        {short.map((f) => (
          <span key={f.key}><span className="text-secondary-500">{f.label}:</span> <span className="font-semibold text-secondary-900">{formatAnswer(f, answers[f.key])}</span></span>
        ))}
      </div>
      {long.map((f) => (
        <p key={f.key} className="mt-1 text-sm text-secondary-800 whitespace-pre-line"><span className="text-secondary-500">{f.label}:</span> {answers[f.key]}</p>
      ))}
    </div>
  );
}

function HomeworkCard({ h, patientId, onChanged, readOnly = false }) {
  const [open, setOpen] = useState(false);
  const [st, cls] = STATUS[h.status] || [h.status, ""];
  const cancel = async () => {
    if (!window.confirm(`Withdraw "${h.title}"? The patient won't see it any more; anything they've filled in is kept.`)) return;
    try {
      await api.cancelHomework(patientId, h.id);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Couldn't withdraw it.");
    }
  };
  return (
    <li className="rounded-xl border border-secondary-200 bg-white">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-start gap-3 p-3 text-left">
        <div className="flex-1 min-w-0">
          <p className="flex flex-wrap items-center gap-1.5">
            <span className="text-sm font-semibold text-secondary-900">{h.title}</span>
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${TYPE_STYLE[h.type] || "bg-secondary-100"}`}>{TYPE_LABEL[h.type] || h.type}</span>
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${cls}`}>{st}</span>
            {h.patient_language && h.patient_language !== "en" && (
              <span className="rounded-full px-2 py-0.5 text-[10px] font-bold bg-violet-50 text-violet-700" title="You see it in English; the patient sees the translation.">Patient sees {LANG[h.patient_language] || h.patient_language.toUpperCase()}</span>
            )}
            {h.responses.length > 0 && <span className="text-[11px] text-secondary-600">{h.responses.length} {h.repeatable ? "entries" : "response"}</span>}
          </p>
          <p className="text-xs text-secondary-600 mt-0.5">
            {h.session_date ? `From the session on ${fmt(h.session_date)}` : `Given ${fmt(h.created_at)}`}{h.assigned_by_name ? ` by ${h.assigned_by_name}` : ""}{h.due_date ? ` · aim to finish by ${fmt(h.due_date)}` : ""}{h.completed_at ? ` · done ${fmt(h.completed_at)}` : ""}
          </p>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-secondary-400 mt-1" /> : <ChevronDown className="w-4 h-4 text-secondary-400 mt-1" />}
      </button>
      {open && (
        <div className="border-t border-secondary-100 p-3 space-y-3">
          {h.note && <p className="rounded-lg bg-secondary-50 px-3 py-2 text-xs text-secondary-700"><span className="font-semibold">Your message:</span> {h.note}</p>}
          {h.patient_language && h.patient_language !== "en" && h.responses.length > 0 && (
            <p className="text-[11px] text-secondary-500">Questions and choices are shown in English. Anything the patient typed is in their own words.</p>
          )}
          {h.responses.length === 0 ? (
            <p className="text-sm text-secondary-500">{h.fields.length ? "Nothing filled in yet." : "Nothing to fill in - this one is to read or practise."}</p>
          ) : (
            <div className="space-y-1.5">{h.responses.map((r) => <Entry key={r.id} fields={h.fields} answers={r.answers} at={r.submitted_at} />)}</div>
          )}
          {!readOnly && (h.status === "assigned" || h.status === "started") && (
            <button type="button" onClick={cancel} className="text-xs font-semibold text-rose-700 hover:underline">Withdraw</button>
          )}
        </div>
      )}
    </li>
  );
}

export default function HomeworkPanel({ patientId, patientName, session = null, noteText = "" }) {
  const [rows, setRows] = useState(null);
  const [toReview, setToReview] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const load = () => api.getPatientHomework(patientId, session ? { consultation_id: session.consultationId || undefined, appointment_id: session.appointmentId || undefined } : {})
    .then((r) => setRows(r.data || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, [patientId, session?.consultationId, session?.appointmentId]); // eslint-disable-line
  useEffect(() => {
    if (!session) return;
    api.getHomeworkToReview(patientId, { consultation_id: session.consultationId || undefined, appointment_id: session.appointmentId || undefined })
      .then((r) => setToReview(r.data || [])).catch(() => setToReview([]));
  }, [patientId, session?.consultationId, session?.appointmentId]); // eslint-disable-line

  const startAssign = () => {
    setAssigning(true);
    if (noteText.trim()) api.suggestTools(noteText).then((r) => setSuggestions(r.data || [])).catch(() => setSuggestions([]));
  };

  return (
    <div className="space-y-3">
      {session && toReview?.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-3 space-y-2 mb-2">
          <div>
            <h3 className="flex items-center gap-2 text-base font-bold text-secondary-900"><ClipboardCheck className="w-5 h-5 text-amber-600" /> To review today</h3>
            <p className="text-xs text-secondary-600">Homework from earlier that's still open, or finished since the last session. Open one to see the patient's answers.</p>
          </div>
          <ul className="space-y-2">{toReview.map((h) => <HomeworkCard key={h.id} h={h} patientId={patientId} readOnly />)}</ul>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="flex items-center gap-2 text-base font-bold text-secondary-900"><BookOpen className="w-5 h-5 text-primary-600" /> Homework{session ? " given in this session" : ""}</h3>
        <button type="button" onClick={startAssign} className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white">
          <Plus className="w-4 h-4" /> Assign homework
        </button>
      </div>
      {rows === null ? <p className="text-sm text-secondary-500">Loading…</p> : rows.length === 0 ? (
        <p className="rounded-xl border border-dashed border-secondary-300 bg-white p-5 text-center text-sm text-secondary-600">
          {session ? "Nothing given in this session yet." : "No homework yet."} Worksheets, exercises and readings you share appear in the patient's My Care.
        </p>
      ) : (
        session ? (
          <ul className="space-y-2">{rows.map((h) => <HomeworkCard key={h.id} h={h} patientId={patientId} onChanged={load} />)}</ul>
        ) : (
          // Patient file: grouped by the session each item came from.
          <div className="space-y-4">
            {[...new Set(rows.map((h) => h.session_date || ""))].map((d) => (
              <div key={d || "none"}>
                <p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-secondary-500">{d ? `Session on ${fmt(d)}` : "Given outside a session"}</p>
                <ul className="space-y-2">{rows.filter((h) => (h.session_date || "") === d).map((h) => <HomeworkCard key={h.id} h={h} patientId={patientId} onChanged={load} />)}</ul>
              </div>
            ))}
          </div>
        )
      )}
      {assigning && (
        <AssignToolModal patientId={patientId} patientName={patientName} consultationId={session?.consultationId} appointmentId={session?.appointmentId}
          suggestions={suggestions} onClose={() => { setAssigning(false); setSuggestions([]); }} onAssigned={load} />
      )}
    </div>
  );
}
